import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';

import { Redirect, Route, Switch, useRouteMatch } from 'react-router';
import { ArchivePanel } from 'src/client/components/Blog/ArchivePanel';
import NewsLetterPanel from '../NewsletterPanel';
import { setTitle } from 'src/client/redux/modules/app';
import LatestComments from '../LatestComments/LatestComments';
import { Post, Posts } from '../index';
import { validateDate, validatePageNumber } from 'src/client/helpers';
import { routes } from 'src/client/utils/router';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import Helmet from '../../Helmet/Helmet';
import { MainPageTitle } from 'src/client/components/layouts/MainPageTitle';
import { useGetBlogTitle } from 'src/client/containers/Blog/Blog/useGetBlogTitle';

const {
  blog: {
    routes: { blog: blogRoute, posts: blogPostsRoute, category: blogCategoryRoute, post: blogPostRoute },
  },
} = routes;

const Div = styled.div`
  padding-top: 30px;

  & .newsletter form input[type='email'] {
    @media (min-width: 992px) and (max-width: 1199px) {
      width: 169px;
    }
  }
`;

const BlogTitle = styled(MainPageTitle)`
  margin-top: 0;
`;

export default function Blog() {
  const dispatch = useDispatch();
  const site = useSelector((state) => state.site);
  const blogCount = useSelector((state) => state.blogs.blogCount);

  useEffect(() => {
    const title = `${site.name} Blog`;
    dispatch(setTitle(title));
  }, [dispatch, site.name]);

  useEffect(() => {
    document.body.classList.add('show-jsd'); // show jsd plugin

    return () => document.body.classList.remove('show-jsd');
  }, []);

  const baseBlogRoute = blogRoute.path;
  const headerTitle = useGetBlogTitle();

  const matchBlogArchive = useRouteMatch<{ pageOrDate?: string }>('/blog/:pageOrDate');
  const pageOrDate = matchBlogArchive?.params.pageOrDate;
  const isValidDate = pageOrDate && pageOrDate.length === 8 && validateDate(pageOrDate);

  const formattedDate = isValidDate
      ? moment(pageOrDate, 'YYYYMMDD').format('MMMM YYYY')
      : '';

  const canonicalUrl = isValidDate
      ? `${window.location.origin}/blog/${pageOrDate}`
      : `${window.location.origin}${baseBlogRoute}`;

  return (
    <Div className="content container">
      <Helmet
        canonicalUrl={canonicalUrl}
        defaultTitle="Erotic Art Archives{{formattedDateSuffix}} | {{domainName}} Blog"
        id="blog"
        variables={{
          domainName: site.domain,
          formattedDateSuffix: formattedDate ? ` ${formattedDate}` : '',
        }}
      />
      {headerTitle && <BlogTitle>{headerTitle}</BlogTitle>}
      <div className="row">
        <Switch>
          <Route
            exact
            path={blogPostsRoute.path}
            render={({ match }) => {
              if (
                !match.params.pageOrDate ||
                (match.params.pageOrDate.length === 8 && validateDate(match.params.pageOrDate)) ||
                validatePageNumber(match.params.pageOrDate)
              ) {
                return <Posts />;
              }

              return <Redirect to={baseBlogRoute} />;
            }}
          />
          <Route
            exact
            path={blogCategoryRoute.path}
            render={({ match }) => {
              if (!match.params.page || validatePageNumber(match.params.page)) {
                return <Posts />;
              }

              return <Redirect to={baseBlogRoute} />;
            }}
          />
          <Route
            exact
            path={blogPostRoute.path}
            render={({ match }) => {
              if (validateDate(match.params.date)) {
                return <Post />;
              }

              return <Redirect to={baseBlogRoute} />;
            }}
          />
          <Redirect to={baseBlogRoute} />
        </Switch>
        <div className="pull-left col-lg-4 col-md-4 col-sm-12 col-xs-12">
          <div className="newsletter">
            <NewsLetterPanel />
          </div>
          <LatestComments />
          <ArchivePanel counts={blogCount} />
        </div>
      </div>
      <div className="clear" />
    </Div>
  );
}
